import React, { useState } from 'react';
import { Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography, Paper, IconButton, Collapse, InputAdornment } from '@mui/material';
import { ExpandMore, ExpandLess, Clear } from '@mui/icons-material';

type EntityType = 'vc_firm' | 'angel_investor' | 'organization' | 'people_of_interest';
interface EntitySearchFormProps {
    onSearch: (query: string, entityType: EntityType) => void;
    initialQuery?: string;
    initialEntityType?: EntityType;
}

const EntitySearchForm: React.FC<EntitySearchFormProps> = ({ onSearch, initialQuery = '', initialEntityType = 'people_of_interest' }) => {
    const [query, setQuery] = useState(initialQuery);
    const [entityType, setEntityType] = useState<EntityType>(initialEntityType);
    const [expanded, setExpanded] = useState(true);

    // Update local state when props change
    React.useEffect(() => {
        setQuery(initialQuery);
    }, [initialQuery]);

    React.useEffect(() => {
        setEntityType(initialEntityType);
    }, [initialEntityType]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch(query, entityType);
    };

    const handleEntityTypeChange = (_e: React.SyntheticEvent, val: EntityType | null) => {
        if (val) {
            setEntityType(val);
            // Trigger search with new entity type
            onSearch(query, val);
        }
    };

    const handleClear = () => {
        setQuery('');
        // Trigger search with empty query to show all results
        onSearch('', entityType);
    };
    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expanded ? 2 : 0 }}>
                <Typography variant="h6">
                    Explore Records
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {expanded ? 'Collapse Search' : 'Expand Search'}
                    </Typography>
                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        aria-label={expanded ? 'collapse' : 'expand'}
                        size="small"
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>
            </Box>
            
            <Collapse in={expanded}>
                <form onSubmit={handleSearch}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Search by name or keyword"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            size="small"
                            InputProps={{
                                endAdornment: query && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={handleClear}
                                            size="small"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 120 }}>
                            Search
                        </Button>
                    </Box>
                    <ToggleButtonGroup
                        color="primary"
                        value={entityType}
                        exclusive
                        onChange={handleEntityTypeChange}
                        sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}
                    >
                        <ToggleButton value="people_of_interest">People of Interest</ToggleButton>
                        <ToggleButton value="vc_firm">Venture Capital Firms</ToggleButton>
                        <ToggleButton value="angel_investor">Angel Investors</ToggleButton>
                        <ToggleButton value="organization">Organizations</ToggleButton>
                    </ToggleButtonGroup>
                </form>
            </Collapse>
        </Paper>
    );
};

export default EntitySearchForm;
